// React imports
import React from 'react'
// Material-UI imports
import { Grid, Typography, Box } from '@mui/material'
// Custom component imports
import Input from 'components/generic/form/Input'
import Checkbox from 'components/generic/form/Checkbox'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import Switch from 'components/generic/form/Switch'
// Utility imports
import { useFormContext } from 'react-hook-form'
// Type imports
import { FreightPreferencesProps } from './types'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

const FreightPreferences: React.FC<FreightPreferencesProps> = ({ accountTypes, index, freightPricing }) => {
  const { watch, control, setValue } = useFormContext()
  const { useMinimalObt } = useAppSelector(state => state.featureFlags?.variables || {})

  const renderPreference = (type: 'supplier' | 'consumer') => {
    const isConsumer = type === 'consumer'
    const title = isConsumer ? 'Consumer Freight Preferences' : 'Supplier Freight Preferences'
    const freightCost = isConsumer ? freightPricing?.consumerFreightCost : freightPricing?.supplierFreightCost
    const defaultEnabled = isConsumer ? freightPricing?.consumerFreightEnabled : freightPricing?.supplierFreightEnabled
    const freightPricingEnabled = watch(`locations.${index}.freightPricing.${type}FreightEnabled`)

    return (
      <>
        <Grid item xs={12} container alignItems={'center'}>
          <Typography variant="body1" color="textSecondary" data-testid={`${type}-freight-preferences-title`}>
            {title}
          </Typography>
          <Switch
            label={watch(`locations.${index}.freightPricing.${type}FreightEnabled`) ? 'Enabled' : 'Disabled'}
            name={`locations.${index}.freightPricing.${type}FreightEnabled`}
            defaultValue={defaultEnabled}
            control={control}
            data-testid={`location-${type}-enabled`}
            variant="outlined"
            sx={{ ml: 2 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ width: '100%' }}>
            <Input
              name={`locations.${index}.freightPricing.${type}FreightCost`}
              label="Freight Cost"
              control={control}
              defaultValue={freightCost}
              disabled={!freightPricingEnabled}
              fullWidth
              variant="outlined"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'price',
                  decimalScale: 2,
                  fixedDecimalScale: true,
                  allowNegative: false,
                  'data-testid': `${type}-freight-cost-input`,
                  'aria-label': `${type} Freight Cost Input`,
                },
              }}
              onBlur={e => {
                if (e.target.value == '') {
                  setValue(`locations.${index}.freightPricing.${type}FreightCost`, '$0.00')
                }
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ width: '100%' }}>
            <Input
              name={`locations.${index}.freightPricing.${type}FreightChargeback`}
              label="Freight Chargeback"
              control={control}
              defaultValue={freightCost}
              disabled={!freightPricingEnabled}
              fullWidth
              variant="outlined"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'price',
                  decimalScale: 2,
                  fixedDecimalScale: true,
                  allowNegative: false,
                  'data-testid': `${type}-freight-chargeback-input`,
                  'aria-label': `${type} Freight Chargeback Input`,
                },
              }}
              onBlur={e => {
                if (e.target.value == '') {
                  setValue(`locations.${index}.freightPricing.${type}FreightChargeback`, '$0.00')
                }
              }}
            />
          </Box>
          {isConsumer && (
            <Checkbox
              name={`locations.${index}.freightPricing.${type}NegativeCharge`}
              control={control}
              label={
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  Yard will pay freight chargeback on invoice
                </Typography>
              }
              disabled={!freightPricingEnabled}
              data-testid={`${type}-negative-charge-checkbox`}
            />
          )}
        </Grid>
      </>
    )
  }

  return (
    <Grid container spacing={2} data-testid="freight-preferences-container">
      {(accountTypes.isConsumer || accountTypes.isSupplierAndConsumer) && renderPreference('consumer')}
      {!useMinimalObt &&
        (accountTypes.isSupplier || accountTypes.isSupplierAndConsumer) &&
        renderPreference('supplier')}
    </Grid>
  )
}

export default FreightPreferences
